<template>
  <vx-card class="mt-base mb-5" :title="activityTitle">
    <vs-tabs alignment="fixed" class="overwrite-tabs">
      <vs-tab label="Verbal Reasoning">
        <div class="mt-5">
          <p class="mb-5">
            Verbal reasoning tests measure your ability to think, reason, and solve problems in
            different ways through the use of language.
          </p>
          <p class="mb-5">
            These tests are aimed at examining your ability to think constructively. There are
            several different types of questions in verbal reasoning tests. These range from
            defining words, providing synonyms and recognising antonyms, through to identifying the
            underlying meanings of common words, phrases, and sentences.
          </p>
          <p class="mb-5">Try your hand at some sample verbal reasoning questions below:</p>
          <div v-for="(question, index) in verbal_reasoning" :key="index">
            <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
            <div class="vx-row my-5">
              <template v-if="isEditable">
                <div
                  class="vx-col w-full mb-2 focusable-parent"
                  v-for="(answer, index2) in question.answers"
                  :key="index2"
                  :class="{
                    wrong:
                      !answer.correct &&
                      answers.verbal_reasoning['question' + index].includes(answer.val),
                  }"
                >
                  <vs-checkbox
                    v-model="answers.verbal_reasoning['question' + index]"
                    class="checkbox_answer multiline focus-visible-style"
                    :vs-value="answer.val"
                    :aria-label="answer.answer"
                  >
                    {{ answer.answer }}
                  </vs-checkbox>
                </div>
              </template>
              <div class="my-2 mx-5" v-else>
                <div
                  class="vx-col w-full"
                  v-for="(answer, index2) in question.answers"
                  :key="index2"
                  :class="{
                    'wrong wrongro':
                      !answer.correct &&
                      answers.verbal_reasoning['question' + index].includes(answer.val),
                  }"
                >
                  <vs-checkbox
                    disabled="true"
                    v-model="answers.verbal_reasoning['question' + index]"
                    class="checkbox_answer multiline"
                    :vs-value="answer.val"
                    >{{ answer.answer }}</vs-checkbox
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </vs-tab>
      <vs-tab label="Abstract Reasoning">
        <div class="mt-5">
          <p class="mb-5">
            Abstract reasoning tests your ability to analyse information, learn and assimilate new
            concepts, and apply knowledge to solve problems on a complex, thought-based level. The
            questions involve identifying patterns and similarities between shapes, figures, and
            diagrams presented in visual form. This means that abstract reasoning isn’t dependent
            upon previous experience or learned verbal or numerical ability.
          </p>
          <div v-for="(question, index) in abstract_reasoning" :key="index">
            <h5 class="mb-5 questionlabel">{{ question.question }}</h5>

            <img :src="question.question_img" class="img-responsive" :alt="question.alt" />
            <div class="vx-row my-5">
              <template v-if="isEditable">
                <div
                  class="vx-col w-full mb-2"
                  :class="{
                    wrong:
                      answers.abstract_reasoning['question' + index] !== 0 &&
                      answers.abstract_reasoning['question' + index] != question.answer,
                  }"
                >
                  <select
                    v-model="answers.abstract_reasoning['question' + index]"
                    class="defaultselect mt-0"
                    style="width: 100%"
                    :aria-label="question.question"
                  >
                    <option value="0">Please Select Answer</option>
                    <option v-for="answer in question.answers_up_to" :key="answer" :value="answer">
                      {{ answer }}
                    </option>
                  </select>
                </div>
              </template>
              <template v-else>
                <div
                  class="vx-col w-full mb-2"
                  :class="{
                    wrong:
                      answers.abstract_reasoning['question' + index] !== 0 &&
                      answers.abstract_reasoning['question' + index] != question.answer,
                  }"
                >
                  <select
                    v-model="answers.abstract_reasoning['question' + index]"
                    class="defaultselect mt-0"
                    disabled="true"
                    style="width: 100%"
                    :aria-label="question.question"
                  >
                    <option value="0">Please Select Answer</option>
                    <option v-for="answer in question.answers_up_to" :key="answer" :value="answer">
                      {{ answer }}
                    </option>
                  </select>
                </div>
              </template>
            </div>
          </div>
        </div>
      </vs-tab>
      <vs-tab label="Mental Arithmetic">
        <div class="mt-5">
          <p class="mb-5">
            The test requires the applicant to answer several mathematical, mental arithmetic
            questions. You’ll need to perform the calculations quickly in your head, so you must get
            plenty of practice beforehand. The more practice you get training your mind to solve
            mental arithmetic style questions, the better your chances of success.
          </p>
          <p class="mb-5">
            Here are a few sample questions for you to practice. Try to complete these within 15
            seconds or less.
          </p>

          <div
            class="vx-row mb-5 questionrow"
            v-for="(question, index) in mental_arithmetic"
            :key="index"
          >
            <div class="vx-col w-3/4 w:sm-full">
              {{ question.question }}
            </div>
            <div class="vx-col w-1/4 w:sm-full text-right">
              <p v-if="showMentalArithmeticAnswers">{{ question.answer }}</p>
              <p v-else>Hidden</p>
            </div>
          </div>
          <button
            class="btn btn-secondary btn-size-medium"
            @click="showMentalArithmeticAnswers = !showMentalArithmeticAnswers"
          >
            {{ showMentalArithmeticAnswers ? 'Hide' : 'Show' }} Answers
          </button>

          <div class="vx-row my-5">
            <div class="vx-col w-full">
              <vs-radio
                vs-name="answers-mental-arithmetic-success"
                v-model="answers.mental_arithmetic.success"
                class="checkbox_answer multiline"
                :vs-value="1"
                :disabled="!isEditable"
                >I was able to complete this task</vs-radio
              >
            </div>
            <div class="vx-col w-full">
              <vs-radio
                vs-name="answers-mental-arithmetic-success"
                v-model="answers.mental_arithmetic.success"
                class="checkbox_answer multiline"
                :vs-value="0"
                :disabled="!isEditable"
                >I struggled to complete this task</vs-radio
              >
            </div>
          </div>

          <p class="mb-5">
            You can continue practising by coming up with your own similar questions before trying
            to answer them against the clock. Remember, practice makes perfect!
          </p>
          <p class="mb-5">
            Sourced from
            <a href="https://www.how2become.com/blog/australian-police-tests/" target="_blank"
              >how2become</a
            >
          </p>
        </div>
      </vs-tab>
      <vs-tab label="Mental Wellbeing">
        <div class="mt-5">
          <p class="mb-5">
            The ability to manage and maintain robust mental and psychological wellbeing is
            important not just for entry to the police service but for ensuring a long and
            productive career within the force.
          </p>
          <p class="mb-5">
            Use the link below to assess yourself on the Warwick-Edinburgh Mental Well-being Scale
            by answering the 14 short questions. After you complete the questionnaire and calculate
            your score, you’ll find a link to some research-based ways you can help maintain and
            improve your mental wellbeing.
          </p>
          <p class="mb-5">
            When you have taken the assessment, record your score in the box below.
          </p>
          <a
            href="https://www.samh.org.uk/about-mental-health/self-help-and-wellbeing/wellbeing-assessment-tool"
            target="_blank"
            class="btn btn-secondary btn-size-medium includedIcon no-underline"
          >
            <upload-icon />Take the Wellbeing Assessment
          </a>
          <br />
          <label>Score:</label>
          <text-block v-model="answers.mental_wellbeing.score" :editable="isEditable" />
        </div>
      </vs-tab>
    </vs-tabs>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import { UploadIcon } from 'vue-feather-icons';

export default {
  components: {
    UploadIcon,
  },
  data() {
    return {
      login_type: localStorage.getItem('login_type'),
      verbal_reasoning: {
        1: {
          question: 'a) Find the word that is most similar in meaning to: Eradicate',
          answers: [
            { val: 1, answer: '1. Procrastinate', correct: false },
            { val: 2, answer: '2. Eliminate', correct: true },
            { val: 3, answer: '3. Subjugate', correct: false },
            { val: 4, answer: '4. Accumulate', correct: false },
          ],
        },
        2: {
          question: 'b) June is to sixth as December is to:',
          answers: [
            { val: 5, answer: '1. Twelfth', correct: true },
            { val: 6, answer: '2. January', correct: false },
            { val: 7, answer: '3. Christmas', correct: false },
            { val: 8, answer: '4. Month', correct: false },
            { val: 9, answer: '5. Winter', correct: false },
          ],
        },
        3: {
          question: 'c) Which two statements mean nearly the same: ',
          answers: [
            { val: 10, answer: '1. Patience is a virtue', correct: true },
            { val: 11, answer: '2. The early bird catches the worm', correct: false },
            { val: 12, answer: '3. Waste not want not', correct: false },
            { val: 13, answer: '4. As you sow so shall you reap', correct: false },
            { val: 14, answer: '5. We must learn to walk before we can run', correct: true },
          ],
        },
        4: {
          question:
            'd) Identify which two statements together prove that George is the oldest boy in his family: ',
          answers: [
            { val: 15, answer: '1. There are four children in the family', correct: false },
            { val: 16, answer: '2. George has two sisters and a brother', correct: true },
            { val: 17, answer: '3. Alan is the youngest sibling', correct: true },
            { val: 18, answer: '4. Sarah likes her brothers', correct: false },
            { val: 19, answer: '5. Alan doesn’t like Sarah', correct: false },
          ],
        },
      },
      abstract_reasoning: {
        1: {
          question: '1. Which piece completes the pattern?',
          question_img: require('@/assets/images/experiences/policing/abstract_1.png'),
          alt: 'A box containing a two-by-two grid of shapes. The top-left cell is empty (dotted outline); the other three cells each contain an outlined upward-pointing arrow. Which piece completes the pattern? Options: 1, a solid triangle; 2, a solid upward-pointing arrow; 3, a solid cross shape; 4, a solid star; 5, an outlined circle; 6, an outlined upward-pointing arrow.',
          answer: 6,
          answers_up_to: 6,
        },
        2: {
          question: '2. Which piece completes the pattern?',
          question_img: require('@/assets/images/experiences/policing/abstract_2.png'),
          alt: 'A box containing a three-by-three grid of shapes. Row 1: a solid circle, a solid square, a solid diamond. Row 2: an outlined diamond, an outlined circle, an outlined square. Row 3: a solid square inside an outlined square, a solid diamond inside an outlined diamond, then an empty cell (dotted outline). Which piece completes the pattern? Options: 1, an outlined square inside a solid square; 2, a solid circle inside an outlined circle; 3, a solid diamond; 4, an outlined diamond inside an outlined diamond; 5, a solid diamond inside an outlined square; 6, an outlined circle inside an outlined circle.',
          answer: 2,
          answers_up_to: 6,
        },
        3: {
          question: '3. Which piece completes the pattern?',
          question_img: require('@/assets/images/experiences/policing/abstract_3.png'),
          alt: 'A box containing a three-by-three grid of solid shapes. Row 1: a small, a medium and a large triangle. Row 2: a small, a medium and a large diamond. Row 3: a small star, a medium star, then an empty cell (dotted outline). Which piece completes the pattern? Options: 1, an outlined star; 2, a small solid triangle; 3, a large solid star; 4, a solid diamond; 5, an outlined hexagon; 6, a solid rectangle.',
          answer: 3,
          answers_up_to: 6,
        },
        4: {
          question: '4. Which piece completes the pattern?',
          question_img: require('@/assets/images/experiences/policing/abstract_4.png'),
          alt: 'A box containing a three-by-three grid of solid shapes. Row 1: a left-pointing arrow, an upward-pointing arrow, a right-pointing triangle. Row 2: a right-pointing arrow, a downward-pointing arrow, a left-pointing triangle. Row 3: a pair of arrows pointing left and right, a pair of arrows pointing up and down, then an empty cell (dotted outline). Which piece completes the pattern? Options: 1, a downward-pointing arrow; 2, a diamond; 3, a right-pointing pentagon; 4, a right-pointing triangle; 5, a right-pointing arrow; 6, a left-pointing triangle; 7, a pair of arrows pointing right and down; 8, a pair of triangles, one pointing right above one pointing left.',
          answer: 8,
          answers_up_to: 8,
        },
      },
      mental_arithmetic: {
        1: {
          question: 'a. 12 x 9',
          answer: '108',
        },
        2: {
          question: 'b. 87 – 23',
          answer: '64',
        },
        3: {
          question: 'c. 79 + 48',
          answer: '127',
        },
        4: {
          question: 'd. 56 + 43',
          answer: '99',
        },
        5: {
          question: 'e. 13 x 11',
          answer: '143',
        },
        6: {
          question: 'f. 19 + 269',
          answer: '288',
        },
      },
      answers: {
        verbal_reasoning: {
          question1: [],
          question2: [],
          question3: [],
          question4: [],
        },
        abstract_reasoning: {
          question1: 0,
          question2: 0,
          question3: 0,
          question4: 0,
        },
        mental_arithmetic: {
          success: null,
        },
        mental_wellbeing: {
          score: null,
        },
      },
      showMentalArithmeticAnswers: false,
    };
  },
  methods: {
    save() {
      this.data.question1 = JSON.stringify(this.answers.verbal_reasoning);
      this.data.question2 = JSON.stringify(this.answers.abstract_reasoning);
      this.data.question3 = JSON.stringify(this.answers.mental_arithmetic);
      this.data.question4 = JSON.stringify(this.answers.mental_wellbeing);
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (this.data.question1) {
      this.answers.verbal_reasoning = JSON.parse(this.data.question1);
    }
    if (this.data.question2) {
      this.answers.abstract_reasoning = JSON.parse(this.data.question2);
    }
    if (this.data.question3) {
      this.answers.mental_arithmetic = JSON.parse(this.data.question3);
    }
    if (this.data.question4) {
      this.answers.mental_wellbeing = JSON.parse(this.data.question4);
    }
  },
};
</script>

<style scoped>
.questionlabel {
  line-height: 1.5;
}
label.isbar_label span {
  color: #d40000;
  font-weight: bold;
}
.checkbox_answer {
  float: left;
  margin: 5px 10px 0 0;
}
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 5px;
  padding-bottom: 8px;
}
.wrongro {
  overflow: hidden;
  padding-top: 0px !important;
  padding-bottom: 5px !important;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
.questionrow {
  background: #f6f6f6;
  padding: 20px 10px;
  margin: 0;
}
</style>
